// src/components/AdminDashboard.js (Firebase COMPAT Firestore)

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { db } from "../firebase"; // compat: firebase.firestore()
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import useAccessProfile from "../hooks/useAccessProfile";
import {
  isActiveUser,
  hasRole,
  canApproveSellerPrep,
  canReviewInspection,
  canManagePickup,
  canManageDelivery,
  canManageTradeIssues,
  canManageRoles,
} from "../utils/accessControl";
import "./AdminDashboard.css";
import TradeWorkflowModal from "./TradeWorkflowModal";

/* ------------------------- helpers ------------------------- */

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();

const toMillisSafe = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    return 0;
  } catch {
    return 0;
  }
};

const toDateStringSafe = (ts) => {
  const ms = toMillisSafe(ts);
  return ms ? new Date(ms).toLocaleString() : "";
};

const stageLabel = (s) => {
  const k = lower(s);
  const MAP = {
    seller_prep: "Seller Prep",
    buyer_approval: "Buyer Approval",
    awaiting_payment: "Awaiting Payment",
    ready_for_inspection: "Ready for Inspection",
    awaiting_dropoff: "Awaiting Drop-off",
    inspection_passed: "Inspection Passed",
    pickup_scheduled: "Pickup Scheduled",
    pickup_in_progress: "Pickup In Progress",
    hub_inspection_pass: "Hub Inspection Passed",
    delivery_scheduled: "Delivery Booked",
    delivery_in_progress: "Delivery In Progress",
    delivered_pending_buyer_verification: "Confirm Handover",
    verification_passed: "Receipt Confirmed",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  return MAP[k] || (k ? k.replace(/_/g, " ") : "—");
};

const formatMoneyZA = (n) => {
  if (typeof n !== "number") return null;
  try {
    return n.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
  } catch {
    return `R ${n}`;
  }
};

const parseMoneyNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const csvEscape = (value) => {
  const s = String(value ?? "");
  return `"${s.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, rows) => {
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
};

const getTradeIdFromWorkflow = (wf) => norm(wf?.tradeId || wf?.tradeRefId || wf?.id || "");
const getWorkflowStage = (wf) => lower(wf?.stage || "");
const getTradeTitle = (wf) => norm(wf?.tradeTitle || wf?.title || wf?.listingTitle || "");
const getPaymentStatus = (wf) => lower(wf?.payment?.status || wf?.cash?.status || "");
const getInspectionFeeStatus = (wf) => lower(wf?.inspectionFee?.status || "");
const getSellerPrepStatus = (wf) => lower(wf?.sellerPrep?.status || "");
const getBuyerApprovalStatus = (wf) => lower(wf?.buyerApproval?.status || "");
const getFulfillmentMethod = (wf) => lower(wf?.fulfillmentChoice?.method || "");
const getDropoffPartnerId = (wf) => lower(wf?.dropoff?.partnerId || "");
const getTradeType = (wf) => lower(wf?.tradeType || wf?.paymentType || "");

const isCashTrade = (wf) => getTradeType(wf) === "cash";
const isPaid = (wf) => getPaymentStatus(wf) === "paid";
const isInspectionFeePaid = (wf) => getInspectionFeeStatus(wf) === "paid";

const isPartnerDropoff = (wf) => {
  const stage = getWorkflowStage(wf);
  const method = getFulfillmentMethod(wf);
  const dropoffPartnerId = getDropoffPartnerId(wf);
  return (
    method === "partner_dropoff" ||
    dropoffPartnerId === "approved_dropoff_point" ||
    stage === "awaiting_dropoff"
  );
};

const isPlatformPickup = (wf) => !isPartnerDropoff(wf);

const resolveWorkflowAction = (wf) => {
  const stage = getWorkflowStage(wf);
  const sellerPrepStatus = getSellerPrepStatus(wf);
  const paid = isPaid(wf);
  const inspectionFeePaid = isInspectionFeePaid(wf);
  const cashTrade = isCashTrade(wf);
  const partnerDropoff = isPartnerDropoff(wf);
  const platformPickup = isPlatformPickup(wf);

  if ((stage === "seller_prep" || stage === "buyer_approval") && sellerPrepStatus === "submitted") {
    return {
      actionable: true,
      permissionKey: "sellerPrepApproval",
      actionKey: "review_seller_prep",
      actionLabel: "Review evidence",
      title: "Seller prep submitted",
      message: "Seller preparation received. Review evidence and approve to proceed.",
    };
  }

  if (stage === "ready_for_inspection" && platformPickup) {
    if (!cashTrade || (paid && inspectionFeePaid)) {
      return {
        actionable: true,
        permissionKey: "pickupManagement",
        actionKey: "schedule_pickup",
        actionLabel: "Schedule pickup",
        title: "Ready for pickup scheduling",
        message:
          "Buyer payment and inspection fee requirements are satisfied. Schedule pickup first. Inspection will happen during pickup.",
      };
    }

    return {
      actionable: false,
      permissionKey: null,
      actionKey: "waiting_payment_or_fee",
      actionLabel: "Waiting for payments",
      title: "Ready for pickup scheduling",
      message: "Waiting for buyer payment and/or seller inspection fee before pickup can be scheduled.",
    };
  }

  if (stage === "awaiting_dropoff" && partnerDropoff) {
    return {
      actionable: true,
      permissionKey: "inspectionReview",
      actionKey: "submit_partner_intake_inspection",
      actionLabel: "Review drop-off intake",
      title: "Awaiting drop-off intake",
      message: "Seller selected approved drop-off point. Complete intake inspection after item drop-off.",
    };
  }

  if (stage === "inspection_passed" && platformPickup) {
    return {
      actionable: true,
      permissionKey: "pickupManagement",
      actionKey: "schedule_pickup",
      actionLabel: "Schedule pickup",
      title: "Inspection passed",
      message: "Inspection passed. Schedule pickup to continue the platform-led process.",
    };
  }

  if (stage === "inspection_passed" && partnerDropoff) {
    return {
      actionable: true,
      permissionKey: "deliveryManagement",
      actionKey: "schedule_delivery",
      actionLabel: "Schedule delivery",
      title: "Inspection passed",
      message: "Approved-point intake passed. Schedule delivery to the buyer.",
    };
  }

  if (stage === "pickup_scheduled") {
    return {
      actionable: true,
      permissionKey: "pickupManagement",
      actionKey: "start_pickup",
      actionLabel: "Start pickup",
      title: "Pickup scheduled",
      message: "Pickup has been scheduled. Start pickup when the collection window begins.",
    };
  }

  if (stage === "pickup_in_progress") {
    return {
      actionable: true,
      permissionKey: "pickupManagement",
      actionKey: "hub_inspection_or_delivery",
      actionLabel: "Open pickup workflow",
      title: "Pickup in progress",
      message: "Pickup is in progress. Continue with hub inspection or delivery scheduling.",
    };
  }

  if (stage === "hub_inspection_pass") {
    return {
      actionable: true,
      permissionKey: "deliveryManagement",
      actionKey: "schedule_delivery",
      actionLabel: "Schedule delivery",
      title: "Hub inspection passed",
      message: "Hub inspection passed. Schedule delivery to the buyer.",
    };
  }

  if (stage === "delivery_scheduled") {
    return {
      actionable: true,
      permissionKey: "deliveryManagement",
      actionKey: "mark_delivered",
      actionLabel: "Record handover",
      title: "Delivery booked",
      message:
        "Delivery is booked. Open the workflow and record handover only once the item has been physically handed to the buyer.",
    };
  }

  if (stage === "delivery_in_progress") {
    return {
      actionable: true,
      permissionKey: "deliveryManagement",
      actionKey: "mark_delivered",
      actionLabel: "Mark delivered",
      title: "Delivery in progress",
      message: "Delivery is in progress. Mark the item as delivered once handover is complete.",
    };
  }

  if (stage === "delivered_pending_buyer_verification") {
    return {
      actionable: false,
      permissionKey: null,
      actionKey: "awaiting_buyer_handover_confirmation",
      actionLabel: "Waiting for buyer",
      title: "Awaiting buyer receipt confirmation",
      message:
        "Delivery handover has been recorded. The buyer must now confirm receipt or report an issue.",
    };
  }

  if (stage === "verification_passed") {
    return {
      actionable: true,
      permissionKey: "tradeIssueManagement",
      actionKey: "complete_trade",
      actionLabel: "Complete trade",
      title: "Buyer verified",
      message: "Buyer verification passed. Complete the trade.",
    };
  }

  if (stage === "completed") {
    return {
      actionable: false,
      permissionKey: null,
      actionKey: "completed",
      actionLabel: "Completed",
      title: "Completed",
      message: "This workflow has already been completed.",
    };
  }

  if (stage === "cancelled") {
    return {
      actionable: false,
      permissionKey: null,
      actionKey: "cancelled",
      actionLabel: "Cancelled",
      title: "Cancelled",
      message: "This workflow has been cancelled.",
    };
  }

  return {
    actionable: false,
    permissionKey: null,
    actionKey: "no_action",
    actionLabel: "Open task",
    title: stageLabel(stage) || "Workflow task",
    message: "Open the workflow to review the current state.",
  };
};

const workflowMatchesSearch = (wf, q) => {
  const search = lower(q);
  if (!search) return true;

  const tradeId = getTradeIdFromWorkflow(wf);
  const title = getTradeTitle(wf);
  const stage = getWorkflowStage(wf);
  const method = getFulfillmentMethod(wf);
  const dropoffName = norm(wf?.dropoff?.partnerName || "");
  const sellerPrepStatus = getSellerPrepStatus(wf);
  const buyerApprovalStatus = getBuyerApprovalStatus(wf);
  const paymentStatus = getPaymentStatus(wf);

  const hay = lower(
    [
      tradeId,
      title,
      stage,
      method,
      dropoffName,
      sellerPrepStatus,
      buyerApprovalStatus,
      paymentStatus,
    ].join(" ")
  );

  return hay.includes(search);
};

const canHandleWorkflowAction = (profile, action) => {
  if (!isActiveUser(profile)) return false;
  if (!action?.actionable) return false;
  if (!action?.permissionKey) return true;
  return Boolean(profile?.permissions?.[action.permissionKey]);
};

const getPromotionIncome = (trade) => {
  const sponsorshipAmount = parseMoneyNumber(
    trade?.sponsorship?.amount ||
      trade?.sponsoredAmount ||
      trade?.sponsorAmount ||
      trade?.promotionAmount ||
      trade?.paidPromotionAmount
  );

  const highlightAmount = parseMoneyNumber(
    trade?.highlightAmount || trade?.promotions?.highlightAmount || trade?.promotionPricing?.highlight
  );

  const urgentAmount = parseMoneyNumber(
    trade?.urgentAmount || trade?.promotions?.urgentAmount || trade?.promotionPricing?.urgent
  );

  const topAdAmount = parseMoneyNumber(
    trade?.topAdAmount || trade?.promotions?.topAdAmount || trade?.promotionPricing?.topAd
  );

  const paymentAmount = parseMoneyNumber(
    trade?.payment?.amount ||
      trade?.payment?.grossAmount ||
      trade?.payfast?.amount ||
      trade?.sponsoredPayment?.amount
  );

  return sponsorshipAmount + highlightAmount + urgentAmount + topAdAmount + paymentAmount;
};

const isTradeSponsored = (trade) =>
  Boolean(
    trade?.sponsored ||
      trade?.isSponsored ||
      trade?.promotions?.sponsored ||
      trade?.sponsorship?.status === "paid" ||
      trade?.sponsoredPayment?.status === "paid"
  );

const isTradeHighlighted = (trade) =>
  Boolean(trade?.promotions?.highlight || trade?.highlighted || trade?.isHighlighted);

const isTradeUrgent = (trade) =>
  Boolean(trade?.promotions?.urgent || trade?.urgent || trade?.isUrgent);

/* ------------------------- component ------------------------- */

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const { profile, loading: accessLoading, exists: accessExists } = useAccessProfile();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("ops");

  const [trades, setTrades] = useState([]);
  const [adverts, setAdverts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeTrades, setFinanceTrades] = useState([]);

  const [editAd, setEditAd] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    timelineFrom: "",
    timelineTo: "",
    status: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortField, setSortField] = useState("createdAt");

  const [advertStatusFilter, setAdvertStatusFilter] = useState("all");
  const [timelineFromFilter, setTimelineFromFilter] = useState("");
  const [timelineToFilter, setTimelineToFilter] = useState("");

  const [moderationLoading, setModerationLoading] = useState(false);
  const [modTrades, setModTrades] = useState([]);
  const [reports, setReports] = useState([]);
  const [riskMap, setRiskMap] = useState({});
  const [modQuery, setModQuery] = useState("");
  const [modStatusFilter, setModStatusFilter] = useState("all");

  const [modActionOpen, setModActionOpen] = useState(false);
  const [modActionBusy, setModActionBusy] = useState(false);
  const [modActionNotes, setModActionNotes] = useState("");
  const [modActionPayload, setModActionPayload] = useState(null);

  const [opsLoading, setOpsLoading] = useState(false);
  const [opsItems, setOpsItems] = useState([]);
  const [opsSearch, setOpsSearch] = useState("");
  const [opsStageFilter, setOpsStageFilter] = useState("all");
  const [opsOnlyActionable, setOpsOnlyActionable] = useState(true);
  const [opsHideCompletedCancelled, setOpsHideCompletedCancelled] = useState(true);

  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowModalTradeId, setWorkflowModalTradeId] = useState("");

  const functions = useMemo(() => getFunctions(), []);
  const adminResolveReport = useMemo(() => httpsCallable(functions, "adminResolveReport"), [functions]);
  const opsUnsubRef = useRef(null);

  const isAdmin = useMemo(() => hasRole(profile, "admin"), [profile]);
  const isFinance = useMemo(() => hasRole(profile, "finance"), [profile]);

  // Finance must be assigned as: roles: ["admin", "finance"]
  const canViewFinance = isAdmin && isFinance;

  const allowedTabs = useMemo(() => {
    const tabs = ["profile"];

    if (
      canApproveSellerPrep(profile) ||
      canReviewInspection(profile) ||
      canManagePickup(profile) ||
      canManageDelivery(profile) ||
      canManageTradeIssues(profile)
    ) {
      tabs.push("ops");
    }

    if (isAdmin) {
      tabs.push("trades", "adverts");
    }

    if (canViewFinance) {
      tabs.push("finance");
    }

    if (canManageTradeIssues(profile)) {
      tabs.push("moderation");
    }

    return tabs;
  }, [profile, isAdmin, canViewFinance]);

  useEffect(() => {
    if (!allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0] || "profile");
    }
  }, [allowedTabs, activeTab]);

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await db.collection("Trades").orderBy("createdAt", "desc").get();
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTrades(data);
    } catch (e) {
      console.error("Failed to fetch trades:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFinanceReports = useCallback(async () => {
    if (!canViewFinance) return;

    setFinanceLoading(true);
    try {
      const snapshot = await db.collection("Trades").orderBy("createdAt", "desc").limit(2000).get();
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setFinanceTrades(data);
    } catch (e) {
      console.error("Failed to fetch finance reports:", e);
      alert("Failed to load finance report.");
    } finally {
      setFinanceLoading(false);
    }
  }, [canViewFinance]);

  const fetchAdverts = useCallback(async () => {
    try {
      const snapshot = await db.collection("Adverts").get();
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAdverts(data);
    } catch (e) {
      console.error("Failed to fetch adverts:", e);
    }
  }, []);

  const fetchModeration = useCallback(async () => {
    setModerationLoading(true);
    try {
      const tradesSnap = await db
        .collection("Trades")
        .where("status", "in", ["flagged", "held", "pending_review"])
        .orderBy("updatedAt", "desc")
        .limit(200)
        .get();

      const flaggedHeld = tradesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setModTrades(flaggedHeld);

      const reportsSnap = await db.collection("TradeReports").orderBy("createdAt", "desc").limit(100).get();
      const reportRows = reportsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReports(reportRows);

      const ids = flaggedHeld.map((t) => t.id).slice(0, 60);
      const pairs = await Promise.all(
        ids.map(async (tradeId) => {
          try {
            const r = await db.collection("TradeRiskSignals").doc(tradeId).get();
            return [tradeId, r.exists ? r.data() : null];
          } catch {
            return [tradeId, null];
          }
        })
      );

      const nextMap = {};
      pairs.forEach(([tradeId, data]) => {
        if (data) nextMap[tradeId] = data;
      });
      setRiskMap(nextMap);
    } catch (e) {
      console.error("Failed to fetch moderation:", e);
    } finally {
      setModerationLoading(false);
    }
  }, []);

  const approvePendingReviewTrade = useCallback(
    async (tradeId) => {
      if (!tradeId) return;
      try {
        await db.collection("Trades").doc(tradeId).update({
          status: "open",
          moderationStatus: "approved",
          moderationDecision: "approved",
          moderationReviewedAt: new Date(),
          updatedAt: new Date(),
          isAvailable: true,
        });
        await fetchModeration();
        await fetchTrades();
      } catch (e) {
        console.error("Failed to approve pending review trade:", e);
        alert("Failed to approve listing.");
      }
    },
    [fetchModeration, fetchTrades]
  );

  const rejectPendingReviewTrade = useCallback(
    async (tradeId) => {
      if (!tradeId) return;
      try {
        await db.collection("Trades").doc(tradeId).update({
          status: "rejected",
          moderationStatus: "rejected",
          moderationDecision: "rejected",
          moderationReviewedAt: new Date(),
          updatedAt: new Date(),
          isAvailable: false,
        });
        await fetchModeration();
        await fetchTrades();
      } catch (e) {
        console.error("Failed to reject pending review trade:", e);
        alert("Failed to reject listing.");
      }
    },
    [fetchModeration, fetchTrades]
  );

  const attachOpsQueue = useCallback(() => {
    if (!isActiveUser(profile)) return;

    const canSeeOps =
      canApproveSellerPrep(profile) ||
      canReviewInspection(profile) ||
      canManagePickup(profile) ||
      canManageDelivery(profile) ||
      canManageTradeIssues(profile);

    if (!canSeeOps) return;

    if (opsUnsubRef.current) {
      try {
        opsUnsubRef.current();
      } catch {}
      opsUnsubRef.current = null;
    }

    setOpsLoading(true);

    const q = db.collection("TradeWorkflows").orderBy("updatedAt", "desc").limit(500);

    const unsub = q.onSnapshot(
      async (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const filtered = rows
          .map((wf) => {
            const tradeId = getTradeIdFromWorkflow(wf) || wf.id;
            const stage = getWorkflowStage(wf);
            const action = resolveWorkflowAction(wf);

            return {
              id: wf.id,
              tradeId,
              stage,
              title: getTradeTitle(wf) || action.title || tradeId,
              wf: { id: wf.id, ...wf },
              action,
              updatedMs: toMillisSafe(wf.updatedAt || wf.lastUpdatedAt || wf.createdAt),
            };
          })
          .filter((row) => !!row.tradeId)
          .filter((row) => {
            if (opsHideCompletedCancelled && (row.stage === "completed" || row.stage === "cancelled")) {
              return false;
            }

            if (opsOnlyActionable && !row.action?.actionable) {
              return false;
            }

            if (opsStageFilter !== "all" && row.stage !== opsStageFilter) {
              return false;
            }

            if (!workflowMatchesSearch(row.wf, opsSearch)) {
              return false;
            }

            if (row.action?.actionable && !canHandleWorkflowAction(profile, row.action)) {
              return false;
            }

            return true;
          })
          .sort((a, b) => (b.updatedMs || 0) - (a.updatedMs || 0));

        setOpsItems(filtered);
        setOpsLoading(false);
      },
      (err) => {
        console.error("Workflow ops queue snapshot error:", err);
        setOpsItems([]);
        setOpsLoading(false);
      }
    );

    opsUnsubRef.current = unsub;
  }, [profile, opsHideCompletedCancelled, opsOnlyActionable, opsSearch, opsStageFilter]);

  useEffect(() => {
    if (!isActiveUser(profile)) return;
    if (activeTab !== "ops") return;

    attachOpsQueue();

    return () => {
      if (opsUnsubRef.current) {
        try {
          opsUnsubRef.current();
        } catch {}
        opsUnsubRef.current = null;
      }
    };
  }, [profile, activeTab, attachOpsQueue]);

  const promoteTrade = useCallback(
    async (id) => {
      try {
        await db.collection("Trades").doc(id).update({
          promotions: { topAd: "31", highlight: true, urgent: true },
        });
        await fetchTrades();
      } catch (e) {
        console.error("Failed to promote trade:", e);
        alert("Failed to promote trade");
      }
    },
    [fetchTrades]
  );

  const deleteTrade = useCallback(
    async (id) => {
      if (!window.confirm("Are you sure you want to delete this trade?")) return;
      try {
        await db.collection("Trades").doc(id).delete();
        await fetchTrades();
      } catch (e) {
        console.error("Failed to delete trade:", e);
        alert("Failed to delete trade");
      }
    },
    [fetchTrades]
  );

  const deleteAdvert = useCallback(
    async (id) => {
      if (!window.confirm("Are you sure you want to delete this advert?")) return;
      try {
        await db.collection("Adverts").doc(id).delete();
        await fetchAdverts();
      } catch (e) {
        console.error("Failed to delete advert:", e);
        alert("Failed to delete advert");
      }
    },
    [fetchAdverts]
  );

  const openEditModal = (ad) => {
    const [from, to] = String(ad.timeline || "").split(" to ");
    setEditAd(ad);
    setEditForm({
      title: ad.title || "",
      description: ad.description || "",
      timelineFrom: from || "",
      timelineTo: to || "",
      status: ad.status || "",
    });
  };

  const handleEditChange = (e) => setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const saveEdit = useCallback(async () => {
    if (!editAd) return;
    try {
      const updatedAd = {
        title: editForm.title,
        description: editForm.description,
        timeline: `${editForm.timelineFrom} to ${editForm.timelineTo}`,
        status: editForm.status,
      };
      await db.collection("Adverts").doc(editAd.id).update(updatedAd);
      setEditAd(null);
      await fetchAdverts();
    } catch (e) {
      console.error("Failed to update advert:", e);
      alert("Failed to update advert");
    }
  }, [editAd, editForm, fetchAdverts]);

  const openModAction = useCallback(({ reportId, action, title, subtitle }) => {
    if (!reportId) {
      alert("No reportId found for this item (missing heldReportId/flaggedReportId).");
      return;
    }
    setModActionPayload({ reportId, action, title, subtitle });
    setModActionNotes("");
    setModActionOpen(true);
  }, []);

  const closeModAction = useCallback(() => {
    if (modActionBusy) return;
    setModActionOpen(false);
    setModActionNotes("");
    setModActionPayload(null);
  }, [modActionBusy]);

  const resolveReport = useCallback(async () => {
    if (!modActionPayload?.reportId || !modActionPayload?.action) return;
    setModActionBusy(true);
    try {
      await adminResolveReport({
        reportId: modActionPayload.reportId,
        action: modActionPayload.action,
        notes: modActionNotes || "",
      });

      setModActionOpen(false);
      setModActionNotes("");
      setModActionPayload(null);

      await fetchModeration();
    } catch (e) {
      console.error("adminResolveReport failed:", e);
      alert(e?.message || "Failed to apply moderation action.");
    } finally {
      setModActionBusy(false);
    }
  }, [adminResolveReport, fetchModeration, modActionPayload, modActionNotes]);

  useEffect(() => {
    if (!isActiveUser(profile)) return;

    if (activeTab === "trades" && isAdmin) fetchTrades();
    if (activeTab === "adverts" && isAdmin) fetchAdverts();
    if (activeTab === "moderation" && canManageTradeIssues(profile)) fetchModeration();
    if (activeTab === "finance" && canViewFinance) fetchFinanceReports();
    if (activeTab === "ops") attachOpsQueue();
  }, [
    profile,
    isAdmin,
    canViewFinance,
    activeTab,
    fetchTrades,
    fetchAdverts,
    fetchModeration,
    fetchFinanceReports,
    attachOpsQueue,
  ]);

  const sortedData = useMemo(() => {
    const base = activeTab === "trades" ? [...trades] : [...adverts];

    base.sort((a, b) => {
      if (sortField === "title") return String(a.title || "").localeCompare(String(b.title || ""));
      if (sortField === "category") return String(a.category || "").localeCompare(String(b.category || ""));
      if (sortField === "promoted") return (b.promotions?.highlight ? 1 : 0) - (a.promotions?.highlight ? 1 : 0);
      return 0;
    });

    return base;
  }, [activeTab, trades, adverts, sortField]);

  const filteredData = useMemo(() => {
    const q = searchTerm.toLowerCase();

    return sortedData.filter((item) => {
      const matchSearch =
        String(item.title || "").toLowerCase().includes(q) ||
        String(item.ownerEmail || "").toLowerCase().includes(q);

      if (activeTab === "trades") {
        const matchCategory = filterCategory === "all" || item.category === filterCategory;
        return matchSearch && matchCategory;
      }

      const matchStatus =
        advertStatusFilter === "all" || String(item.status || "").toLowerCase() === advertStatusFilter.toLowerCase();

      const [fromStr, toStr] = String(item.timeline || "").split(" to ");
      const fromDate = fromStr ? new Date(fromStr) : null;
      const toDate = toStr ? new Date(toStr) : null;

      const fromFilter = timelineFromFilter ? new Date(timelineFromFilter) : null;
      const toFilter = timelineToFilter ? new Date(timelineToFilter) : null;

      const matchTimeline =
        (!fromFilter || (fromDate && fromDate >= fromFilter)) &&
        (!toFilter || (toDate && toDate <= toFilter));

      return matchSearch && matchStatus && matchTimeline;
    });
  }, [sortedData, searchTerm, activeTab, filterCategory, advertStatusFilter, timelineFromFilter, timelineToFilter]);

  const moderationStats = useMemo(() => {
    const held = modTrades.filter((t) => String(t.status || "").toLowerCase() === "held").length;
    const flagged = modTrades.filter((t) => String(t.status || "").toLowerCase() === "flagged").length;
    const pendingReview = modTrades.filter((t) => String(t.status || "").toLowerCase() === "pending_review").length;
    const newReports = reports.filter((r) => String(r.status || "new").toLowerCase() === "new").length;

    return { held, flagged, pendingReview, newReports };
  }, [modTrades, reports]);

  const filteredModTrades = useMemo(() => {
    const q = modQuery.trim().toLowerCase();

    return modTrades
      .filter((t) => {
        const s = String(t.status || "").toLowerCase();
        if (modStatusFilter === "all") return true;
        return s === modStatusFilter;
      })
      .filter((t) => {
        if (!q) return true;
        return (
          String(t.title || "").toLowerCase().includes(q) ||
          String(t.category || "").toLowerCase().includes(q) ||
          String(t.id || "").toLowerCase().includes(q) ||
          String(t.heldReason || t.flaggedReason || "").toLowerCase().includes(q)
        );
      });
  }, [modTrades, modQuery, modStatusFilter]);

  const opsStats = useMemo(() => {
    const total = opsItems.length;
    const byStage = {};
    opsItems.forEach((n) => {
      const s = n.stage || "unknown";
      byStage[s] = (byStage[s] || 0) + 1;
    });
    return { total, byStage };
  }, [opsItems]);

  const financeStats = useMemo(() => {
    const totalTrades = financeTrades.length;
    const sponsored = financeTrades.filter(isTradeSponsored).length;
    const highlighted = financeTrades.filter(isTradeHighlighted).length;
    const urgent = financeTrades.filter(isTradeUrgent).length;

    const totalIncome = financeTrades.reduce((sum, trade) => sum + getPromotionIncome(trade), 0);

    const openTrades = financeTrades.filter((t) => lower(t.status) === "open").length;
    const completedTrades = financeTrades.filter((t) => lower(t.status) === "completed").length;
    const pendingReviewTrades = financeTrades.filter((t) => lower(t.status) === "pending_review").length;

    return {
      totalTrades,
      sponsored,
      highlighted,
      urgent,
      totalIncome,
      openTrades,
      completedTrades,
      pendingReviewTrades,
    };
  }, [financeTrades]);

  const exportFinanceCsv = useCallback(() => {
    const rows = [
      [
        "Trade ID",
        "Title",
        "Category",
        "Owner Email",
        "Status",
        "Created At",
        "Sponsored",
        "Highlighted",
        "Urgent",
        "Income",
      ],
      ...financeTrades.map((trade) => [
        trade.id,
        trade.title || "",
        trade.category || "",
        trade.ownerEmail || trade.owner?.email || "",
        trade.status || "",
        toDateStringSafe(trade.createdAt),
        isTradeSponsored(trade) ? "Yes" : "No",
        isTradeHighlighted(trade) ? "Yes" : "No",
        isTradeUrgent(trade) ? "Yes" : "No",
        getPromotionIncome(trade),
      ]),
      [],
      ["Summary"],
      ["Total Trades", financeStats.totalTrades],
      ["Sponsored Trades", financeStats.sponsored],
      ["Highlighted Trades", financeStats.highlighted],
      ["Urgent Promotions", financeStats.urgent],
      ["Total Income", financeStats.totalIncome],
    ];

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`finance-report-${stamp}.csv`, rows);
  }, [financeTrades, financeStats]);

  const openWorkflowModal = useCallback((tradeId) => {
    if (!tradeId) return;
    setWorkflowModalTradeId(tradeId);
    setWorkflowModalOpen(true);
  }, []);

  if (accessLoading) return <p>Loading access profile...</p>;

  if (!accessExists) {
    return <p>⛔ Access profile not provisioned. Please contact a super admin.</p>;
  }

  if (!isActiveUser(profile)) {
    return <p>⛔ Your account is inactive. Please contact an administrator.</p>;
  }

  if (!isAdmin) {
    return <p>⛔ Access Denied. Admin role required.</p>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-hero">
        <div>
          <div className="admin-hero-title">Admin Console</div>
          <div className="admin-hero-subtitle">NexterZone operations, listings and moderation</div>
        </div>
        <div className="admin-hero-pill">
          <span className="dot" />
          Signed in: <strong style={{ marginLeft: 6 }}>{currentUser?.email}</strong>
        </div>
      </div>

      <nav className="admin-menu">
        <div className="menu-buttons">
          {allowedTabs.includes("ops") && (
            <button className={`admin-tab-btn ${activeTab === "ops" ? "active" : ""}`} onClick={() => setActiveTab("ops")}>
              ✅ Ops Queue
            </button>
          )}

          <button className={`admin-tab-btn ${activeTab === "profile" ? "active" : ""}`} onClick={() => setActiveTab("profile")}>
            👤 Profile
          </button>

          {allowedTabs.includes("trades") && (
            <button className={`admin-tab-btn ${activeTab === "trades" ? "active" : ""}`} onClick={() => setActiveTab("trades")}>
              📦 Trades
            </button>
          )}

          {allowedTabs.includes("adverts") && (
            <button className={`admin-tab-btn ${activeTab === "adverts" ? "active" : ""}`} onClick={() => setActiveTab("adverts")}>
              📢 Adverts
            </button>
          )}

          {allowedTabs.includes("finance") && (
            <button className={`admin-tab-btn ${activeTab === "finance" ? "active" : ""}`} onClick={() => setActiveTab("finance")}>
              💰 Finance
            </button>
          )}

          {allowedTabs.includes("moderation") && (
            <button className={`admin-tab-btn ${activeTab === "moderation" ? "active" : ""}`} onClick={() => setActiveTab("moderation")}>
              🛡️ Moderation
            </button>
          )}
        </div>

        {isAdmin && (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {canManageRoles(profile) && (
              <Link
                to="/admin/user-access"
                className="btn btn-soft"
                style={{ textDecoration: "none" }}
              >
                👥 Manage User Access
              </Link>
            )}

            <Link
              to="/admin/tools"
              className="btn btn-primary"
              style={{ textDecoration: "none" }}
            >
              🏪 New Partner Store
            </Link>
          </div>
        )}
      </nav>

      {activeTab !== "profile" && activeTab !== "moderation" && activeTab !== "ops" && activeTab !== "finance" && (
        <div className="admin-filters">
          <label>
            🔍 Search
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>

          {activeTab === "trades" && (
            <label>
              🗂 Category
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="all">All</option>
                <option value="Furniture">Furniture</option>
                <option value="Electronics">Electronics</option>
                <option value="Vehicles">Vehicles</option>
                <option value="Fashion">Fashion</option>
                <option value="Books">Books</option>
                <option value="Home & Garden">Home & Garden</option>
              </select>
            </label>
          )}

          {activeTab === "adverts" && (
            <>
              <label>
                📌 Status
                <select value={advertStatusFilter} onChange={(e) => setAdvertStatusFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="expired">Expired</option>
                </select>
              </label>

              <label>
                🗓 From
                <input type="date" value={timelineFromFilter} onChange={(e) => setTimelineFromFilter(e.target.value)} />
              </label>

              <label>
                🗓 To
                <input type="date" value={timelineToFilter} onChange={(e) => setTimelineToFilter(e.target.value)} />
              </label>
            </>
          )}

          <label>
            📊 Sort
            <select value={sortField} onChange={(e) => setSortField(e.target.value)}>
              <option value="createdAt">Most Recent</option>
              <option value="title">Title A–Z</option>
              <option value="category">Category A–Z</option>
              <option value="promoted">Promoted First</option>
            </select>
          </label>
        </div>
      )}

      {activeTab === "ops" && (
        <div className="admin-filters">
          <label>
            🔎 Search tasks
            <input
              type="text"
              placeholder="Search tradeId, title, stage, method…"
              value={opsSearch}
              onChange={(e) => setOpsSearch(e.target.value)}
            />
          </label>

          <label>
            🧭 Stage
            <select value={opsStageFilter} onChange={(e) => setOpsStageFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="seller_prep">seller_prep</option>
              <option value="ready_for_inspection">ready_for_inspection</option>
              <option value="awaiting_dropoff">awaiting_dropoff</option>
              <option value="inspection_passed">inspection_passed</option>
              <option value="pickup_scheduled">pickup_scheduled</option>
              <option value="pickup_in_progress">pickup_in_progress</option>
              <option value="hub_inspection_pass">hub_inspection_pass</option>
              <option value="delivery_scheduled">delivery_scheduled</option>
              <option value="delivered_pending_buyer_verification">
                delivered_pending_buyer_verification
              </option>
              <option value="delivery_in_progress">delivery_in_progress</option>
              <option value="verification_passed">verification_passed</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={opsOnlyActionable} onChange={(e) => setOpsOnlyActionable(e.target.checked)} />
            Only actionable
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={opsHideCompletedCancelled}
              onChange={(e) => setOpsHideCompletedCancelled(e.target.checked)}
            />
            Hide completed/cancelled
          </label>

          <button className="btn btn-soft" onClick={attachOpsQueue} disabled={opsLoading}>
            🔄 Refresh
          </button>
        </div>
      )}

      <div className="admin-content">
        {activeTab === "finance" && (
          <div className="admin-card">
            <div className="admin-card-header">
              <div>
                <h2>Finance Dashboard</h2>
                <p className="muted">
                  Activity and revenue reporting for trade listings, sponsored posts and promotions.
                </p>
              </div>

              <div className="header-actions">
                <button className="btn btn-soft" onClick={fetchFinanceReports} disabled={financeLoading}>
                  🔄 Refresh
                </button>

                <button className="btn btn-primary" onClick={exportFinanceCsv} disabled={financeLoading || financeTrades.length === 0}>
                  📤 Export CSV
                </button>
              </div>
            </div>

            {financeLoading ? (
              <p className="muted">Loading finance report…</p>
            ) : (
              <>
                <div className="stats">
                  <div className="stat">
                    <div className="stat-k">Total trades posted</div>
                    <div className="stat-v">{financeStats.totalTrades}</div>
                  </div>

                  <div className="stat">
                    <div className="stat-k">Sponsored trades</div>
                    <div className="stat-v">{financeStats.sponsored}</div>
                  </div>

                  <div className="stat">
                    <div className="stat-k">Highlighted trades</div>
                    <div className="stat-v">{financeStats.highlighted}</div>
                  </div>

                  <div className="stat">
                    <div className="stat-k">Urgent promotions</div>
                    <div className="stat-v">{financeStats.urgent}</div>
                  </div>

                  <div className="stat">
                    <div className="stat-k">Open trades</div>
                    <div className="stat-v">{financeStats.openTrades}</div>
                  </div>

                  <div className="stat">
                    <div className="stat-k">Completed trades</div>
                    <div className="stat-v">{financeStats.completedTrades}</div>
                  </div>

                  <div className="stat">
                    <div className="stat-k">Pending review</div>
                    <div className="stat-v">{financeStats.pendingReviewTrades}</div>
                  </div>

                  <div className="stat">
                    <div className="stat-k">Total income</div>
                    <div className="stat-v">{formatMoneyZA(financeStats.totalIncome) || "R 0.00"}</div>
                  </div>
                </div>

                <h3 className="section-title">Recent finance activity</h3>

                {financeTrades.length === 0 ? (
                  <p className="muted">No finance records found yet.</p>
                ) : (
                  <div className="grid">
                    {financeTrades.slice(0, 80).map((trade) => {
                      const income = getPromotionIncome(trade);

                      return (
                        <div key={trade.id} className="card">
                          <div className="card-top">
                            <div className="title">{trade.title || "(Untitled trade)"}</div>
                            <div className="badges">
                              {isTradeSponsored(trade) && <span className="badge badge-green">Sponsored</span>}
                              {isTradeHighlighted(trade) && <span className="badge badge-gold">Highlighted</span>}
                              {isTradeUrgent(trade) && <span className="badge badge-red">Urgent</span>}
                              {trade.status && <span className="badge badge-gray">{String(trade.status)}</span>}
                            </div>
                          </div>

                          <div className="meta">
                            <div>
                              <span className="meta-k">Category:</span> {trade.category || "—"}
                            </div>
                            <div>
                              <span className="meta-k">Owner:</span> {trade.ownerEmail || trade.owner?.email || "—"}
                            </div>
                            <div>
                              <span className="meta-k">Created:</span> {toDateStringSafe(trade.createdAt) || "—"}
                            </div>
                            <div>
                              <span className="meta-k">Income:</span> {formatMoneyZA(income) || "R 0.00"}
                            </div>
                            <div className="small-muted">TradeId: {trade.id}</div>
                          </div>

                          <div className="actions actions-wrap">
                            <Link className="btn btn-soft" to={`/trade/${encodeURIComponent(trade.id)}`} style={{ textDecoration: "none" }}>
                              🔎 Open listing
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "ops" && (
          <div className="admin-card">
            <div className="admin-card-header">
              <div>
                <h2>Ops Queue</h2>
                <p className="muted">
                  Workflow-driven admin tasks across seller prep, inspection, pickup, delivery and completion.
                </p>
              </div>
              <div className="header-actions">
                <span className="badge badge-gold" title="Total workflow tasks shown">
                  {opsStats.total} tasks
                </span>
              </div>
            </div>

            {opsLoading ? (
              <p className="muted">Loading workflow ops queue…</p>
            ) : opsItems.length === 0 ? (
              <p className="muted">No workflow tasks right now.</p>
            ) : (
              <div className="grid">
                {opsItems.map((item) => {
                  const { wf, tradeId, stage, title, action, updatedMs } = item;
                  const method = getFulfillmentMethod(wf);
                  const paymentStatus = getPaymentStatus(wf);
                  const inspectionFeeStatus = getInspectionFeeStatus(wf);
                  const partnerName = norm(wf?.dropoff?.partnerName || "");
                  const cashValue =
                    typeof wf?.amount === "number"
                      ? formatMoneyZA(wf.amount)
                      : typeof wf?.cashAmount === "number"
                      ? formatMoneyZA(wf.cashAmount)
                      : null;

                  return (
                    <div key={item.id} className="card">
                      <div className="card-top">
                        <div className="title">{action?.title || title || tradeId}</div>
                        <div className="badges">
                          {stage ? <span className="badge badge-gray">{stageLabel(stage)}</span> : null}
                          {method ? <span className="badge badge-gray">{method}</span> : null}
                          {action?.actionable ? (
                            <span className="badge badge-gold">Action required</span>
                          ) : (
                            <span className="badge badge-gray">View</span>
                          )}
                        </div>
                      </div>

                      <div className="meta">
                        <div>{action?.message || "Open the workflow to review the current state."}</div>
                        <div className="small-muted">TradeId: {tradeId || "—"}</div>
                        {getTradeTitle(wf) ? <div className="small-muted">Title: {getTradeTitle(wf)}</div> : null}
                        {cashValue ? <div className="small-muted">Amount: {cashValue}</div> : null}
                        {paymentStatus ? <div className="small-muted">Payment: {paymentStatus}</div> : null}
                        {inspectionFeeStatus ? <div className="small-muted">Inspection fee: {inspectionFeeStatus}</div> : null}
                        {partnerName ? <div className="small-muted">Drop-off point: {partnerName}</div> : null}
                        {updatedMs ? <div className="small-muted">Updated: {new Date(updatedMs).toLocaleString()}</div> : null}
                      </div>

                      <div className="actions actions-wrap">
                        <button
                          className="btn btn-primary"
                          onClick={() => openWorkflowModal(tradeId)}
                          title="Open the admin workflow to complete this step"
                        >
                          ✅ {action?.actionLabel || "Open workflow"}
                        </button>

                        <button
                          className="btn btn-soft"
                          type="button"
                          onClick={() => openWorkflowModal(tradeId)}
                          title="View workflow"
                        >
                          🔎 View workflow
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "profile" && (
          <div className="admin-card">
            <div className="admin-card-header">
              <div>
                <h2>Admin Profile</h2>
                <p className="muted">Access is enforced by user roles and permissions.</p>
              </div>
            </div>
            <div className="admin-kv">
              <div className="kv">
                <div className="k">Email</div>
                <div className="v">{currentUser?.email}</div>
              </div>
              <div className="kv">
                <div className="k">Role</div>
                <div className="v">{profile?.roles?.join(", ") || "—"}</div>
              </div>
              <div className="kv">
                <div className="k">Inspector Type</div>
                <div className="v">{profile?.staffProfile?.inspectorType || "—"}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "trades" && (
          <div className="admin-card">
            <div className="admin-card-header">
              <div>
                <h2>Trades</h2>
                <p className="muted">Promote for visibility, or remove inappropriate listings.</p>
              </div>
              <button className="btn btn-soft" onClick={fetchTrades}>
                🔄 Refresh
              </button>
            </div>

            {loading ? (
              <p className="muted">Loading trades…</p>
            ) : (
              <div className="grid">
                {filteredData.map((trade) => (
                  <div key={trade.id} className="card">
                    <div className="card-top">
                      <div className="title">{trade.title || "(Untitled)"}</div>
                      <div className="badges">
                        {trade.promotions?.highlight && <span className="badge badge-gold">Promoted</span>}
                        {trade.promotions?.urgent && <span className="badge badge-red">Urgent</span>}
                        {trade.status && <span className="badge badge-gray">{String(trade.status)}</span>}
                      </div>
                    </div>

                    <div className="meta">
                      <div>
                        <span className="meta-k">Category:</span> {trade.category || "—"}
                      </div>
                      <div>
                        <span className="meta-k">Owner:</span> {trade.ownerEmail || "—"}
                      </div>
                      <div>
                        <span className="meta-k">Price:</span> {formatMoneyZA(trade.price) || "—"}
                      </div>
                    </div>

                    <div className="actions">
                      <button onClick={() => promoteTrade(trade.id)} className="btn btn-primary">
                        ⭐ Promote
                      </button>
                      <button onClick={() => deleteTrade(trade.id)} className="btn btn-danger">
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "adverts" && (
          <div className="admin-card">
            <div className="admin-card-header">
              <div>
                <h2>Adverts</h2>
                <p className="muted">Manage announcements and promotional banners.</p>
              </div>
              <div className="header-actions">
                <Link to="/upload-ad" className="btn btn-primary" style={{ textDecoration: "none" }}>
                  ➕ Post Advert
                </Link>
                <button className="btn btn-soft" onClick={fetchAdverts}>
                  🔄 Refresh
                </button>
              </div>
            </div>

            <div className="grid">
              {filteredData.map((ad) => (
                <div key={ad.id} className="card">
                  <div className="card-top">
                    <div className="title">{ad.title || "(Untitled advert)"}</div>
                    <div className="badges">
                      {ad.status && (
                        <span
                          className={`badge ${
                            String(ad.status).toLowerCase() === "active"
                              ? "badge-green"
                              : String(ad.status).toLowerCase() === "pending"
                              ? "badge-gold"
                              : "badge-gray"
                          }`}
                        >
                          {String(ad.status)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="meta">
                    <div>
                      <span className="meta-k">Timeline:</span> {ad.timeline || "—"}
                    </div>
                    <div className="desc">{ad.description || "—"}</div>
                  </div>

                  <div className="actions">
                    <button onClick={() => openEditModal(ad)} className="btn btn-soft">
                      ✏️ Edit
                    </button>
                    <button onClick={() => deleteAdvert(ad.id)} className="btn btn-danger">
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "moderation" && (
          <div className="admin-card">
            <div className="admin-card-header">
              <div>
                <h2>Moderation</h2>
                <p className="muted">Flagged/held trades and recent user reports.</p>
              </div>
              <div className="header-actions">
                <button className="btn btn-primary" onClick={fetchModeration} disabled={moderationLoading}>
                  🔄 Refresh
                </button>
              </div>
            </div>

            <div className="stats">
              <div className="stat">
                <div className="stat-k">Held</div>
                <div className="stat-v">{moderationStats.held}</div>
              </div>
              <div className="stat">
                <div className="stat-k">Flagged</div>
                <div className="stat-v">{moderationStats.flagged}</div>
              </div>
              <div className="stat">
                <div className="stat-k">New reports</div>
                <div className="stat-v">{moderationStats.newReports}</div>
              </div>
              <div className="stat">
                <div className="stat-k">Pending review</div>
                <div className="stat-v">{moderationStats.pendingReview}</div>
              </div>
            </div>

            <div className="moderation-controls">
              <label>
                🔎 Search
                <input
                  type="text"
                  placeholder="Search title, category, tradeId, reason…"
                  value={modQuery}
                  onChange={(e) => setModQuery(e.target.value)}
                />
              </label>

              <label>
                🧰 Status
                <select value={modStatusFilter} onChange={(e) => setModStatusFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="pending_review">Pending review</option>
                  <option value="held">Held</option>
                  <option value="flagged">Flagged</option>
                </select>
              </label>
            </div>

            {moderationLoading ? (
              <p className="muted">Loading moderation queue…</p>
            ) : (
              <>
                <h3 className="section-title">Flagged / Held trades</h3>

                {filteredModTrades.length === 0 ? (
                  <p className="muted">No trades currently flagged or held.</p>
                ) : (
                  <div className="grid">
                    {filteredModTrades.map((t) => {
                      const sig = riskMap[t.id];
                      const status = String(t.status || "").toLowerCase();
                      const reportId = t.heldReportId || t.flaggedReportId || null;

                      return (
                        <div key={t.id} className="card">
                          <div className="card-top">
                            <div className="title">{t.title || "(Untitled trade)"}</div>
                            <div className="badges">
                              <span className={`badge ${status === "held" ? "badge-red" : "badge-gold"}`}>
                                {status || "unknown"}
                              </span>
                              {t.category && <span className="badge badge-gray">{t.category}</span>}
                            </div>
                          </div>

                          <div className="meta">
                            {status === "pending_review" && (
                              <>
                                <div>
                                  <span className="meta-k">Moderation status:</span> {t.moderationStatus || "pending_review"}
                                </div>
                                <div>
                                  <span className="meta-k">Moderation decision:</span> {t.moderationDecision || "pending_review"}
                                </div>
                                <div>
                                  <span className="meta-k">Moderation note:</span> {t.moderationMessage || "Awaiting admin review"}
                                </div>
                              </>
                            )}

                            {status === "flagged" && (
                              <div>
                                <span className="meta-k">Flag reason:</span> {t.flaggedReason || "—"}
                              </div>
                            )}

                            {status === "held" && (
                              <div>
                                <span className="meta-k">Hold reason:</span> {t.heldReason || "—"}
                              </div>
                            )}

                            {sig && (
                              <div>
                                <span className="meta-k">Reports (24h):</span> unique {sig.unique24h ?? "—"} / total{" "}
                                {sig.total24h ?? "—"}
                              </div>
                            )}

                            <div className="small-muted">TradeId: {t.id}</div>
                          </div>

                          <div className="actions actions-wrap">
                            <Link className="btn btn-soft" to={`/trade/${encodeURIComponent(t.id)}`} style={{ textDecoration: "none" }}>
                              🔎 Open listing
                            </Link>

                            {status === "pending_review" ? (
                              <>
                                <button className="btn btn-primary" onClick={() => approvePendingReviewTrade(t.id)}>
                                  ✅ Approve listing
                                </button>

                                <button className="btn btn-danger" onClick={() => rejectPendingReviewTrade(t.id)}>
                                  ⛔ Reject listing
                                </button>
                              </>
                            ) : (
                              <>
                                <Link className="btn btn-soft" to={`/report/${encodeURIComponent(t.id)}`} style={{ textDecoration: "none" }}>
                                  🧾 Open report page
                                </Link>

                                <button
                                  className="btn btn-primary"
                                  onClick={() =>
                                    openModAction({
                                      reportId,
                                      action: "release_hold",
                                      title: "Release hold",
                                      subtitle: `Trade: ${t.title || t.id}`,
                                    })
                                  }
                                  disabled={!reportId}
                                  title={!reportId ? "No reportId attached to this trade yet." : ""}
                                >
                                  ✅ Release hold
                                </button>

                                <button
                                  className="btn btn-danger"
                                  onClick={() =>
                                    openModAction({
                                      reportId,
                                      action: "keep_hold",
                                      title: "Keep hold",
                                      subtitle: `Trade: ${t.title || t.id}`,
                                    })
                                  }
                                  disabled={!reportId}
                                  title={!reportId ? "No reportId attached to this trade yet." : ""}
                                >
                                  ⛔ Keep hold
                                </button>

                                <button
                                  className="btn btn-soft"
                                  onClick={() =>
                                    openModAction({
                                      reportId,
                                      action: "dismiss_report",
                                      title: "Dismiss report",
                                      subtitle: `Trade: ${t.title || t.id}`,
                                    })
                                  }
                                  disabled={!reportId}
                                  title={!reportId ? "No reportId attached to this trade yet." : ""}
                                >
                                  🧹 Dismiss
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <h3 className="section-title" style={{ marginTop: 18 }}>
                  Recent reports
                </h3>

                {reports.length === 0 ? (
                  <p className="muted">No reports yet.</p>
                ) : (
                  <div className="grid">
                    {reports.slice(0, 50).map((r) => (
                      <div key={r.id} className="card">
                        <div className="card-top">
                          <div className="title">{r.reason ? `Report: ${r.reason}` : "Report"}</div>
                          <div className="badges">
                            <span className={`badge ${String(r.status || "new").toLowerCase() === "new" ? "badge-gold" : "badge-gray"}`}>
                              {String(r.status || "new")}
                            </span>
                          </div>
                        </div>

                        <div className="meta">
                          <div>
                            <span className="meta-k">Trade:</span> {r.tradeId || "—"}
                          </div>
                          <div className="small-muted">
                            {r.tradeSnapshot?.title ? `"${r.tradeSnapshot.title}"` : "(no title)"}{" "}
                            {r.tradeSnapshot?.category ? `• ${r.tradeSnapshot.category}` : ""}
                          </div>
                        </div>

                        <div className="actions actions-wrap">
                          {r.tradeId && (
                            <Link className="btn btn-soft" to={`/trade/${encodeURIComponent(r.tradeId)}`} style={{ textDecoration: "none" }}>
                              🔎 Open listing
                            </Link>
                          )}

                          <button
                            className="btn btn-primary"
                            onClick={() =>
                              openModAction({
                                reportId: r.id,
                                action: "release_hold",
                                title: "Release hold",
                                subtitle: `Report: ${r.reason || "—"} • Trade: ${r.tradeId || "—"}`,
                              })
                            }
                          >
                            ✅ Release hold
                          </button>

                          <button
                            className="btn btn-danger"
                            onClick={() =>
                              openModAction({
                                reportId: r.id,
                                action: "keep_hold",
                                title: "Keep hold",
                                subtitle: `Report: ${r.reason || "—"} • Trade: ${r.tradeId || "—"}`,
                              })
                            }
                          >
                            ⛔ Keep hold
                          </button>

                          <button
                            className="btn btn-soft"
                            onClick={() =>
                              openModAction({
                                reportId: r.id,
                                action: "dismiss_report",
                                title: "Dismiss report",
                                subtitle: `Report: ${r.reason || "—"} • Trade: ${r.tradeId || "—"}`,
                              })
                            }
                          >
                            🧹 Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {editAd && (
        <div className="adminmodal-overlay">
          <div className="adminmodal-content">
            <h3>Edit Advert</h3>

            <label>
              Title:
              <input name="title" value={editForm.title} onChange={handleEditChange} />
            </label>

            <label>
              Description:
              <textarea name="description" value={editForm.description} onChange={handleEditChange} />
            </label>

            <div className="row">
              <label>
                From:
                <input type="date" name="timelineFrom" value={editForm.timelineFrom} onChange={handleEditChange} />
              </label>

              <label>
                To:
                <input type="date" name="timelineTo" value={editForm.timelineTo} onChange={handleEditChange} />
              </label>
            </div>

            <label>
              Status:
              <input name="status" value={editForm.status} onChange={handleEditChange} />
            </label>

            <div className="modal-buttons">
              <button onClick={saveEdit} className="btn btn-primary">
                💾 Save
              </button>
              <button onClick={() => setEditAd(null)} className="btn btn-soft">
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {modActionOpen && modActionPayload && (
        <div className="adminmodal-overlay" onMouseDown={closeModAction}>
          <div
            className="adminmodal-content"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modActionTitle"
          >
            <div className="adminmodal-header">
              <div>
                <h3 id="modActionTitle" style={{ margin: 0 }}>
                  {modActionPayload.title}
                </h3>
                <div className="muted" style={{ marginTop: 6 }}>
                  {modActionPayload.subtitle}
                </div>
              </div>
              <button className="icon-btn" onClick={closeModAction} disabled={modActionBusy} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="adminmodal-body">
              <div className="callout">
                <strong>Action:</strong> <span style={{ textTransform: "none" }}>{modActionPayload.action}</span>
                <div className="callout-sub">Notes are stored internally for audit and support. They are not shown to customers.</div>
              </div>

              <label>
                Admin notes (optional)
                <textarea
                  value={modActionNotes}
                  onChange={(e) => setModActionNotes(e.target.value)}
                  placeholder="Add context: why you released/kept the hold, what evidence was checked, etc."
                />
              </label>
            </div>

            <div className="modal-buttons">
              <button className="btn btn-soft" onClick={closeModAction} disabled={modActionBusy}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={resolveReport} disabled={modActionBusy}>
                {modActionBusy ? "Applying…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <TradeWorkflowModal
        open={workflowModalOpen}
        onClose={() => {
          setWorkflowModalOpen(false);
          setWorkflowModalTradeId("");
        }}
        tradeId={workflowModalTradeId}
        mode="admin"
      />
    </div>
  );
};

export default AdminDashboard;